import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getToken, messaging } from '../../utils/firebase.js';
import { useRegisterPushTokenMutation } from '../../api/user.js';
import { useSelector } from 'react-redux';
import BaseOverlay from '../../components/layout/BaseOverlay.jsx';
import NotificationButton from '../../components/misc/notifications/NotificationButton.jsx';
import BaseButton from '../../components/form/BaseButton.jsx';

const PushNotificationManager = () => {
    const [registerPushToken] = useRegisterPushTokenMutation();
    const user = useSelector((state) => state.user.user);
    const hasTokenRegistered = useRef(false);
    const [showIosPrompt, setShowIosPrompt] = useState(false);
    const [showOverlay, setShowOverlay] = useState(false);

    const isIOSSafari = () => {
        const ua = navigator.userAgent || window.navigator.userAgent;
        const isIOS = /iP(hone|od|ad)/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPadOS 13+

        const isSafari = /WebKit/.test(ua) && !/CriOS/.test(ua) && !/FxiOS/.test(ua);

        return isIOS && isSafari;
    };

    const isPwaInstalled = () => {
        const wn = navigator || window.navigator;
        const isStandalone = wn.standalone === true;
        const isDisplayModeStandalone = window.matchMedia('(display-mode: standalone)').matches;

        return isStandalone || isDisplayModeStandalone;
    };

    const register = useCallback(async () => {
        if (!isPwaInstalled()) {
            return;
        }

        const waitForServiceWorkerActivation = async (registration) => {
            if (registration.active) return;
            await new Promise((resolve) => {
                const sw = registration.installing || registration.waiting;
                if (sw) {
                    sw.addEventListener('statechange', function () {
                        if (sw.state === 'activated') resolve();
                    });
                }
            });
        };

        try {
            if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
                throw new Error('Push notifications not supported in this browser.');
            }

            const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

            await waitForServiceWorkerActivation(registration);

            if (!navigator.serviceWorker.controller) {
                await new Promise((resolve) => navigator.serviceWorker.addEventListener('controllerchange', resolve));
            }

            const permission = await Notification.requestPermission();

            if (permission !== 'granted') {
                throw new Error('Notification permission denied');
            }

            const token = await getToken(messaging, {
                vapidKey: import.meta.env.VITE_VAPID_PUBLIC_KEY,
                serviceWorkerRegistration: registration
            });

            if (!token) throw new Error('FCM token is null or undefined');

            await registerPushToken({ type: 'fcm', data: token });
            hasTokenRegistered.current = true;
        } catch (error) {
            console.error('Push registration failed:', error);
        }
    }, [registerPushToken]);

    const handleIosNotifications = async (action = true) => {
        if (action) {
            setShowOverlay(false);

            try {
                if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
                    throw new Error('Push notifications not supported in this browser.');
                }

                const registration = await navigator.serviceWorker.ready;

                const permission = await Notification.requestPermission();

                if (permission !== 'granted') {
                    throw new Error('Notification permission denied');
                }

                const urlBase64ToUint8Array = (base64String) => {
                    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
                    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
                    const raw = atob(base64);
                    return new Uint8Array([...raw].map((char) => char.charCodeAt(0)));
                };

                const sub = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(import.meta.env.VITE_VAPID_PUBLIC_KEY)
                });

                await registerPushToken({ type: 'webpush', data: JSON.stringify(sub) });

                localStorage.setItem('safariPushPrompted', 'true');

                setShowIosPrompt(false);
            } catch (error) {
                console.error('Push registration failed:', error);
            }
        } else {
            setShowOverlay(false);
            setShowIosPrompt(false);
            localStorage.setItem('safariPushPrompted', 'true');
        }
    };

    useEffect(() => {
        (async () => {
            if (!user || hasTokenRegistered.current) return;

            if (isPwaInstalled() && isIOSSafari()) {
                const promptedBefore = localStorage.getItem('safariPushPrompted') === 'true';
                if (!promptedBefore) {
                    setShowIosPrompt(true);
                }
            } else {
                await register();
            }
        })();
    }, [user, register]);

    return (
        <>
            {showIosPrompt && (
                <>
                    <NotificationButton onClick={() => setShowOverlay(true)} />
                    <BaseOverlay show={showOverlay} className='overflow-auto h-[100dvh] rounded-none'>
                        <div className='min-h-96 max-w-3xl w-full mx-auto px-8'>
                            <div className='flex justify-end py-4'>
                                <div
                                    className='text-theme1-primary font-bold cursor-pointer'
                                    onClick={() => setShowOverlay(false)}
                                >
                                    Close
                                </div>
                            </div>

                            <div className='text-xl font-bold text-theme1-primary2 mb-4 pb-3 border-b border-solid border-theme1-tanTransparent text-center'>
                                Enable push notifications
                            </div>

                            <div className='mb-10'>
                                <div className='grid grid-cols-2 gap-4'>
                                    <BaseButton
                                        id='btn-submit'
                                        className='btn-primary'
                                        onClick={handleIosNotifications}
                                        textValue='Allow'
                                    />

                                    <BaseButton
                                        id='btn-cancel'
                                        className='btn-primary-outline'
                                        onClick={() => handleIosNotifications(false)}
                                        textValue='Deny and hide'
                                    />
                                </div>
                            </div>
                        </div>
                    </BaseOverlay>
                </>
            )}
        </>
    );
};

export default PushNotificationManager;
